import React, { createContext, useContext, useState, useEffect } from "react";
import translations from "../translations/translations";
import { auth, db } from "../firebase";
import { doc, updateDoc } from "firebase/firestore";

const LanguageContext = createContext();

const LANG_KEY = "pinpoint_language";

const LANG_OPTIONS = [
  { value: "en", label: "EN", flag: "🇬🇧", dir: "ltr" },
  { value: "fr", label: "FR", flag: "🇫🇷", dir: "ltr" },
  { value: "ar", label: "AR", flag: "🇦🇪", dir: "rtl" },
];

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored && translations[stored]) return stored;
    return "en";
  });

  // Apply RTL/LTR to entire document whenever language changes
  useEffect(() => {
    const option = LANG_OPTIONS.find(l => l.value === lang);
    document.documentElement.dir  = option?.dir || "ltr";
    document.documentElement.lang = lang;
  }, [lang]);

  // Called from anywhere to change language
  // Saves to localStorage immediately; saves to Firestore in background (non-blocking)
  async function setLang(newLang) {
    if (!translations[newLang]) return;
    localStorage.setItem(LANG_KEY, newLang);
    setLangState(newLang);

    // Save to Firestore if a user is logged in — never block if this fails
    const user = auth.currentUser;
    if (user) {
      try {
        const token     = await user.getIdTokenResult(false); // use cached token, fast
        const companyId = token.claims.companyId;
        const docId     = token.claims.docId || user.uid;
        if (companyId) {
          await updateDoc(doc(db, "companies", companyId, "users", docId), { language: newLang });
        }
      } catch (err) {
        console.error("[LANG] Failed to save language to Firestore:", err.message);
      }
    }
  }

  // t("key") — returns translation or falls back to English or the key itself
  function t(key) {
    return translations[lang]?.[key] ?? translations["en"]?.[key] ?? key;
  }

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, LANG_OPTIONS }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
}

export default LanguageContext;
